
import { getCourierAssignPage, getCourierOpLogPage } from '@/api/record'
import { orderRefund, queryOrderDetailById, deliveryOrder, completeOrder } from '@/api/order'
import UserNameRemark from '@/components/UserNameRemark/index.vue'
import AdminCourierTransferDialog from '@/components/AdminCourierTransferDialog/index.vue'
import {
  formatMoney,
  payMethodLabel,
  dishLineTotal,
  orderDishSubtotalFrom,
  orderQuotaDiscountFrom,
  orderDiscountLabel,
  hasOrderDiscount,
} from '@/utils/orderAmount'
import { formatOrderBizType } from '@/utils/orderBizType'
import { formatOrderRemarkDisplay } from '@/utils/orderRemarkDisplay'
import { isBucketWaterDeliveryOrder as checkBucketWaterOrder, formatEmptyBucketIntentText } from '@/utils/bucketWaterOrder'
import { scheduleElTableFixedSync } from '@/utils/elTableFixedSync'

/**
 * 派送记录「退款」开关（便于测试来回切换）：
 * 1) 改下面默认值后重新编译；或
 * 2) 浏览器控制台（无需改代码）：
 *    localStorage.setItem('courierAssignShowRefund', '1')  // 显示
 *    localStorage.setItem('courierAssignShowRefund', '0')  // 隐藏
 *    然后刷新本页
 */
const COURIER_ASSIGN_SHOW_REFUND_DEFAULT = false

function readCourierAssignShowRefund(): boolean {
  try {
    const v = localStorage.getItem('courierAssignShowRefund')
    if (v === '1' || v === 'true') return true
    if (v === '0' || v === 'false') return false
  } catch (e) {}
  return COURIER_ASSIGN_SHOW_REFUND_DEFAULT
}

export default {
  name: 'CourierAssignLog',
  components: { UserNameRemark, AdminCourierTransferDialog },
  data() {
    return {
      showRefundActions: readCourierAssignShowRefund(),
      activeTab: 'current',
      loading: false,
      list: [] as any[],
      logList: [] as any[],
      total: 0,
      query: {
        pageNum: 1,
        pageSize: 10,
        userPhone: '',
        orderNumber: '',
        courierPhone: '',
        status: '' as number | string,
      },
      statusOptions: [
        { label: '全部', value: '' },
        { label: '待派送', value: 3 },
        { label: '派送中', value: 4 },
        { label: '已完成', value: 5 },
      ],
      transferVisible: false,
      transferOrderId: null as number | string | null,
      transferHasCourier: false,
      refundVisible: false,
      refundSubmitting: false,
      refundOrderId: null as number | string | null,
      refundStoreId: null as number | null,
      refundReason: '',
      refundRemark: '',
      refundReasonList: [
        { value: 1, label: '订单量较多，暂时无法接单' },
        { value: 2, label: '商品已销售完，暂时无法接单' },
        { value: 3, label: '骑手不足无法配送' },
        { value: 4, label: '客户电话取消' },
        { value: 0, label: '自定义原因' },
      ],
      viewVisible: false,
      viewLoading: false,
      viewDetail: null as any,
      viewListRow: null as any,
      actionSubmitting: false,
    }
  },
  computed: {
    dialogOrderStatus(): number {
      const d = this.viewDetail as any
      if (d && d.status != null) return Number(d.status)
      if (this.viewListRow && this.viewListRow.status != null) return Number(this.viewListRow.status)
      return 0
    },
    storeDisplayName(): string {
      const d = this.viewDetail as any
      if (!d) return '—'
      if (d.shopName) return d.shopName
      if (d.storeId != null) return `商户${d.storeId}`
      return '—'
    },
    currentCourierLabel(): string {
      return this.courierNameOf(this.viewDetail)
    },
    hasCourier(): boolean {
      const d = this.viewDetail as any
      const row = this.viewListRow as any
      return !!(d && d.courierId) || !!(row && row.courierId)
    },
    orderDishSubtotal(): string {
      return orderDishSubtotalFrom(this.viewDetail)
    },
    orderQuotaDiscount(): string {
      return orderQuotaDiscountFrom(this.viewDetail)
    },
    showOrderDiscount(): boolean {
      return hasOrderDiscount(this.viewDetail)
    },
    orderDiscountText(): string {
      return orderDiscountLabel(this.viewDetail)
    },
    emptyBucketIntentText(): string {
      return formatEmptyBucketIntentText(this.viewDetail)
    },
    payMethodText(): string {
      const d = this.viewDetail as any
      return payMethodLabel(d && d.payMethod, d)
    },
    showMixedPaySplit(): boolean {
      const form = this.viewDetail as any
      if (!form) return false
      const balance = Number(form.balancePayAmount)
      const online = Number(form.onlinePayAmount)
      return Number.isFinite(balance) && balance > 0 && Number.isFinite(online) && online > 0
    },
    onlinePayChannelName(): string {
      const form = this.viewDetail as any
      const m = Number(form && form.payMethod)
      return m === 3 ? '支付宝支付' : '微信支付'
    },
  },
  mounted() {
    this.loadList()
    window.addEventListener('resize', this.scheduleTableLayout)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.scheduleTableLayout)
  },
  methods: {
    scheduleTableLayout() {
      scheduleElTableFixedSync(this, 'currentTable')
    },
    formatOrderBizType,
    formatOrderMoney(val: any) {
      return formatMoney(val)
    },
    dishLineAmount(item: any) {
      return dishLineTotal(item)
    },
    giftList(item: any) {
      const list = item && item.gifts
      return Array.isArray(list) ? list.filter(Boolean) : []
    },
    displayOrderRemark(remark: string) {
      return formatOrderRemarkDisplay(remark) || '—'
    },
    isBucketWaterDeliveryOrder(row: any) {
      return checkBucketWaterOrder(row)
    },
    formatAdminGiftLog(log: any): string {
      if (!log) return ''
      const qty = Number(log.giftQty)
      const unit = (log.unit && String(log.unit).trim()) || '桶'
      const qtyText = Number.isFinite(qty) && qty > 0 ? `${Math.floor(qty)} ${unit}` : `— ${unit}`
      let timeText = ''
      const t = log.giftTime
      if (t != null) {
        if (typeof t === 'string') {
          timeText = t.length >= 16 ? t.slice(0, 16).replace('T', ' ') : t.replace('T', ' ')
        } else if (Array.isArray(t) && t.length >= 5) {
          const pad = (n: number) => (n < 10 ? `0${n}` : String(n))
          timeText = `${t[0]}-${pad(t[1])}-${pad(t[2])} ${pad(t[3])}:${pad(t[4])}`
        }
      }
      const name = log.dishName ? `（${log.dishName}）` : ''
      return `${timeText ? timeText + ' ' : ''}赠送 ${qtyText}${name}`.trim()
    },
    onTabChange() {
      this.query.pageNum = 1
      this.loadList()
    },
    async loadList() {
      this.loading = true
      try {
        const params: any = {
          pageNum: this.query.pageNum,
          pageSize: this.query.pageSize,
        }
        if (this.query.userPhone) params.userPhone = this.query.userPhone.trim()
        if (this.query.orderNumber) params.orderNumber = this.query.orderNumber.trim()
        if (this.query.courierPhone) params.courierPhone = this.query.courierPhone.trim()
        if (this.activeTab === 'current' && this.query.status !== '' && this.query.status != null) {
          params.status = Number(this.query.status)
        }

        if (this.activeTab === 'current') {
          const res: any = await getCourierAssignPage(params)
          if (String(res.data.code) === '1' && res.data.data) {
            this.list = res.data.data.records || []
            this.total = res.data.data.total || 0
          } else {
            this.list = []
            this.total = 0
          }
        } else {
          params.actionType = this.activeTab === 'assign' ? 1 : -1
          const res: any = await getCourierOpLogPage(params)
          if (String(res.data.code) === '1' && res.data.data) {
            this.logList = res.data.data.records || []
            this.total = res.data.data.total || 0
          } else {
            this.logList = []
            this.total = 0
          }
        }
      } finally {
        this.loading = false
        this.scheduleTableLayout()
      }
    },
    handleSearch() {
      this.query.pageNum = 1
      this.loadList()
    },
    handleReset() {
      this.query = {
        pageNum: 1,
        pageSize: 10,
        userPhone: '',
        orderNumber: '',
        courierPhone: '',
        status: '',
      }
      this.loadList()
    },
    canAssign(row: any) {
      if (!row) return false
      const oid = row.orderId != null ? row.orderId : row.id
      if (oid == null) return false
      const st = Number(row.status)
      if (st !== 3 && st !== 4) return false
      if (row.payStatus != null && Number(row.payStatus) !== 1) return false
      return true
    },
    canRefund(row: any) {
      if (!this.showRefundActions) return false
      if (!row) return false
      const oid = row.orderId != null ? row.orderId : row.id
      if (oid == null) return false
      const st = Number(row.status)
      if (![3, 4, 5].includes(st)) return false
      if (row.payStatus != null && Number(row.payStatus) !== 1) return false
      return true
    },
    openTransfer(row: any) {
      if (!row) return
      const oid = row.orderId != null ? row.orderId : row.id
      if (oid == null) return
      this.transferOrderId = oid
      this.transferHasCourier = !!(row.courierId)
      this.$nextTick(() => {
        this.transferVisible = true
      })
    },
    onTransferSuccess() {
      this.transferVisible = false
      this.transferOrderId = null
      this.loadList()
    },
    openRefund(row: any) {
      if (!row) return
      const oid = row.orderId != null ? row.orderId : row.id
      if (oid == null) return
      this.refundOrderId = oid
      this.refundStoreId = row.storeId != null ? Number(row.storeId) : null
      this.refundReason = ''
      this.refundRemark = ''
      this.refundVisible = true
    },
    closeRefund() {
      this.refundVisible = false
      this.refundOrderId = null
      this.refundStoreId = null
      this.refundReason = ''
      this.refundRemark = ''
      this.refundSubmitting = false
    },
    async confirmRefund() {
      if (!this.refundReason) {
        this.$message.error('请选择退款原因')
        return
      }
      if (this.refundReason === '自定义原因' && !this.refundRemark) {
        this.$message.error('请输入退款原因')
        return
      }
      if (this.refundOrderId == null) return
      this.refundSubmitting = true
      try {
        const payload: any = {
          id: this.refundOrderId,
          cancelReason:
            this.refundReason === '自定义原因' ? this.refundRemark : this.refundReason,
        }
        if (this.refundStoreId != null) payload.storeId = this.refundStoreId
        const res: any = await orderRefund(payload)
        if (res && res.data && res.data.code === 1) {
          this.$message.success('操作成功')
          this.closeRefund()
          this.viewVisible = false
          this.loadList()
        } else {
          this.$message.error((res && res.data && res.data.msg) || '退款失败')
        }
      } catch (e: any) {
        this.$message.error((e && e.message) || '退款失败')
      } finally {
        this.refundSubmitting = false
      }
    },
    async openView(row: any) {
      if (!row || row.orderId == null) return
      this.viewListRow = row
      this.viewVisible = true
      this.viewLoading = true
      this.viewDetail = null
      try {
        const res: any = await queryOrderDetailById({ orderId: row.orderId })
        const body = res && res.data
        if (!body || Number(body.code) !== 1 || !body.data) {
          this.$message.error((body && body.msg) || '加载详情失败')
          return
        }
        const d = { ...body.data }
        if (!d.courierId && row.courierId) d.courierId = row.courierId
        if (!d.courierName && row.courierName) d.courierName = row.courierName
        if (!d.courierPhone && row.courierPhone) d.courierPhone = row.courierPhone
        if (d.id == null) d.id = row.orderId
        if (d.orderId == null) d.orderId = row.orderId
        if (d.storeId == null && row.storeId != null) d.storeId = row.storeId
        this.viewDetail = d
      } catch (e: any) {
        this.$message.error((e && e.message) || '加载详情失败')
      } finally {
        this.viewLoading = false
      }
    },
    onViewClosed() {
      this.viewDetail = null
      this.viewListRow = null
    },
    openTransferFromView() {
      const row = this.viewListRow || this.viewDetail
      if (!row) return
      this.openTransfer({
        orderId: row.orderId != null ? row.orderId : row.id,
        courierId: row.courierId || (this.viewListRow && this.viewListRow.courierId),
        status: row.status,
        payStatus: row.payStatus != null ? row.payStatus : 1,
      })
    },
    openRefundFromView() {
      const row = this.viewListRow || this.viewDetail
      if (!row) return
      this.openRefund({
        orderId: row.orderId != null ? row.orderId : row.id,
        storeId: row.storeId != null ? row.storeId : (this.viewListRow && this.viewListRow.storeId),
        status: row.status,
        payStatus: row.payStatus != null ? row.payStatus : 1,
      })
    },
    statusText(status: any, row?: any) {
      const n = Number(status)
      // 兼容旧数据：有派送员仍记为 3 时，展示为派送中
      if (n === 3 && row && row.courierId) return '派送中'
      const map: Record<number, string> = {
        1: '待付款',
        2: '待接单',
        3: '待派送',
        4: '派送中',
        5: '已完成',
        6: '已取消',
        7: '已退款',
      }
      return map[n] || (status != null ? String(status) : '—')
    },
    courierNameOf(detail: any) {
      if (!detail) return '—'
      const name = detail.courierName || (this.viewListRow && this.viewListRow.courierName)
      const phone = detail.courierPhone || (this.viewListRow && this.viewListRow.courierPhone)
      const n = name != null ? String(name).trim() : ''
      const p = phone != null ? String(phone).trim() : ''
      if (!n && !p) return '待派单'
      return p ? `${n || '派送员'}（${p}）` : n
    },
    canForceComplete(row: any) {
      if (!row) return false
      const st = Number(row.status)
      return (st === 3 || st === 4) && this.isBucketWaterDeliveryOrder(row)
    },
    async forceCompleteRow(row: any) {
      if (!row) return
      try {
        await this.$confirm(
          '该桶装水订单将由管理员强制标记为已完成，并按订单桶数登记空桶。确认继续？',
          '强制完成',
          { type: 'warning', confirmButtonText: '确认完成', cancelButtonText: '取消' }
        )
      } catch (_) {
        return
      }
      const id = row.orderId != null ? row.orderId : row.id
      const storeId = row.storeId
      if (id == null || storeId == null) {
        this.$message.error('缺少订单或店铺信息')
        return
      }
      try {
        const res: any = await completeOrder({ id, storeId })
        if (res && res.data && Number(res.data.code) === 1) {
          this.$message.success('已完成')
          this.loadList()
        } else {
          this.$message.error((res && res.data && res.data.msg) || '操作失败')
        }
      } catch (e: any) {
        this.$message.error((e && e.message) || '操作失败')
      }
    },
    async doDeliveryOrComplete(kind: 3 | 4) {
      const d = this.viewDetail as any
      if (!d) return
      const id = d.id != null ? d.id : d.orderId
      const storeId = d.storeId != null ? d.storeId : (this.viewListRow && this.viewListRow.storeId)
      if (id == null || storeId == null) {
        this.$message.error('缺少订单或店铺信息')
        return
      }
      if (kind === 4 && this.isBucketWaterDeliveryOrder(d)) {
        try {
          await this.$confirm(
            '该桶装水订单将由管理员强制标记为已完成，并按订单桶数登记空桶。确认继续？',
            '强制完成',
            { type: 'warning', confirmButtonText: '确认完成', cancelButtonText: '取消' }
          )
        } catch (_) {
          return
        }
      }
      this.actionSubmitting = true
      try {
        const api = kind === 3 ? deliveryOrder : completeOrder
        const res: any = await api({ id, storeId })
        if (res && res.data && Number(res.data.code) === 1) {
          this.$message.success(kind === 3 ? '已派送' : '已完成')
          this.viewVisible = false
          this.loadList()
        } else {
          this.$message.error((res && res.data && res.data.msg) || '操作失败')
        }
      } catch (e: any) {
        this.$message.error((e && e.message) || '操作失败')
      } finally {
        this.actionSubmitting = false
      }
    },
    courierLabel(row: any, side: 'from' | 'to') {
      if (!row) return '—'
      if (Number(row.actionType) === 1 && side === 'from') {
        return '系统'
      }
      if (Number(row.actionType) === 3 && side === 'to') {
        return row.toCourierName || '待派单（管理员）'
      }
      const name = side === 'from' ? row.fromCourierName : row.toCourierName
      const phone = side === 'from' ? row.fromCourierPhone : row.toCourierPhone
      const n = name != null ? String(name).trim() : ''
      const p = phone != null ? String(phone).trim() : ''
      if (!n && !p) return '—'
      const label = n || '派送员'
      return p ? `${label}（${p}）` : label
    },
    formatOperator(row: any, _column: any, cellValue: any) {
      const op = cellValue != null && cellValue !== '' ? String(cellValue).trim() : ''
      if (op) return op
      if (!row) return '—'
      if (Number(row.actionType) === 1) return '系统'
      const from = row.fromCourierName != null ? String(row.fromCourierName).trim() : ''
      const fromPhone = row.fromCourierPhone != null ? String(row.fromCourierPhone).trim() : ''
      if (from || fromPhone) {
        const label = from || '派送员'
        return fromPhone ? `${label}（${fromPhone}）` : label
      }
      return '—'
    },
  },
}
